import classNames from 'classnames';
import { useConsentContext } from 'contexts/ConsentContext';
import React from 'react';
import { useIntl } from 'react-intl';
import { Link as RouterLink, useRouteMatch } from 'react-router-dom';
import { ReactComponent as HamburgerCloseIcon } from 'static/icons/hamburger_close_button.svg';
import { ReactComponent as HamburgerIcon } from 'static/icons/hamburger_icon.svg';
import WartsilaLogo from 'static/icons/wartsila_logo.png';

import isExternalLink from '../../../utils/isExternalLink';
import styles from './HamburgerMenu.module.scss';
import { useRoutes } from './shared';

interface HamburgerItemProps {
    to: string;
    text: string | React.ReactElement<any>;
    icon: React.ReactElement<any>;
    onClickFunction(setBoolean: boolean): void;
}

interface HamburgerContainerProps {
    className?: string;
    onClickFunction(setBoolean: boolean): void;
}

interface HamburgerMenuProps {
    className?: string;
    setIsOpen(setBoolean: boolean): void;
    isOpen: boolean;
}

const HamburgerItem = ({ to, text, icon, onClickFunction }: HamburgerItemProps) => {
    const match = useRouteMatch({
        path: to,
        strict: true,
        sensitive: true,
    });

    const isActive = match?.path === "/" ? match.isExact : match;
    const newTab = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        onClickFunction(false);
        if (isExternalLink(to)) {
            window.open(to);
            e.preventDefault();
        }
    };
    return (
        <div className={styles["item-div"]}>
            <li
                className={classNames(styles["item"], {
                    [styles["item--active"]]: isActive,
                })}
            >
                <RouterLink onClick={newTab} to={to} className={styles["item__link"]}>
                    {React.cloneElement(icon, {
                        className: classNames(styles["item__icon"], {
                            [styles["item__icon--active"]]: isActive,
                        }),
                    })}
                    <span
                        className={classNames(styles["item__text"], {
                            [styles["item__text--active"]]: isActive,
                        })}
                    >
                        {text}
                    </span>
                </RouterLink>
            </li>
        </div>
    );
};

const HamburgerContainer = ({ className, onClickFunction }: HamburgerContainerProps) => {
    const routes = useRoutes();
    const intl = useIntl();
    const { reOpen } = useConsentContext();

    return (
        <div className={classNames(styles["hamburger-menu-container"], className)}>
            <ul className={styles["hamburger-menu-container__list"]}>
                {routes.map((route) => {
                    return (
                        <HamburgerItem
                            to={route.to}
                            text={route.text}
                            icon={route.icon}
                            key={route.to}
                            onClickFunction={onClickFunction}
                        />
                    );
                })}
            </ul>

            <div className={classNames(styles["hamburger-menu-container__footer"])}>
                <img
                    src={WartsilaLogo}
                    alt="Wartsila Logo"
                    className={styles["hamburger-menu-container__footer__logo"]}
                />
                <button
                    onClick={reOpen}
                    className={styles["hamburger-menu-container__footer__privacy_notice"]}
                >
                    {intl.formatMessage({ id: "view_name.privacy_notice" })}
                </button>
            </div>
        </div>
    );
};

const HamburgerMenu = ({ className, setIsOpen, isOpen }: HamburgerMenuProps) => {
    return (
        <>
            <button
                type="button"
                className={className}
                aria-label={"HamburgerMenuButton"}
                onClick={() => setIsOpen(!isOpen)}
            >
                {isOpen ? <HamburgerCloseIcon /> : <HamburgerIcon />}
            </button>
            {isOpen && <HamburgerContainer className={className} onClickFunction={setIsOpen} />}
        </>
    );
};

export default HamburgerMenu;
